import React, { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Modal, ButtonBase } from '@mui/material';
import { Search } from '@mui/icons-material';
import { CSSTransition } from 'react-transition-group';
import SearchResultsList from './SearchResultsList';
import { setActiveInsight } from '../../store/slices/layoutSlice';
import { Insight } from '../../pages/insights/types';

type TransitionProps = {
  in?: boolean;
  width?: number;
  onEnter?: () => void;
  onExited?: () => void;
  onEntered?: () => void;
  children?: React.ReactNode;
};

const Transition = React.forwardRef(
  (
    {
      in: inProp,
      width,
      children,
      onEnter,
      onEntered,
      onExited,
    }: TransitionProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    return (
      <CSSTransition
        nodeRef={modalRef}
        in={inProp}
        timeout={300}
        classNames='modal'
        onEnter={onEnter}
        onExited={onExited}
        onEntered={onEntered}
      >
        <Box
          ref={modalRef}
          sx={(theme) => ({
            backgroundColor: 'primary.main',
            boxShadow: 8,
            position: 'absolute',
            top: 2,
            left: '50%',
            transform: 'translateX(-50%)',
            width: width ?? '400px',
            overflow: 'hidden',
            borderRadius: 2.5,
            '&.modal-enter': {
              opacity: 0.5,
              maxHeight: 32,
            },
            '&.modal-enter-active': {
              maxHeight: '500px',
              opacity: 1,
              transition: theme.transitions.create(['max-height', 'opacity']),
            },
            '&.modal-enter-done': {
              maxHeight: '500px',
              opacity: 1,
            },
            '&.modal-exit': {
              maxHeight: '500px',
              opacity: 1,
            },
            '&.modal-exit-active': {
              maxHeight: 32,
              opacity: 0,
              transition: theme.transitions.create(['max-height', 'opacity']),
            },
            '&.modal-exit-done': {
              maxHeight: 32,
              opacity: 0,
              pointerEvents: 'none',
            },
          })}
        >
          <Box ref={ref} tabIndex={-1}>
            {children}
          </Box>
        </Box>
      </CSSTransition>
    );
  }
);

export default function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInsightClick = useCallback(
    (insight: Insight) => {
      setIsOpen(false);
      dispatch(setActiveInsight(insight));
      history.push(`/views/${insight.id}`);
    },
    [dispatch, history]
  );

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    buttonRef.current?.focus();
  }, []);

  const onSearchButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.blur();
      openModal();
    },
    [openModal]
  );

  return (
    <Box
      sx={{
        flex: 1,
        flexGrow: 1.5,
        width: '100%',
      }}
    >
      <ButtonBase
        ref={buttonRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'Roboto, sans-serif',
          width: '100%',
          px: 1,
          py: 0.5,
          bgcolor: 'primary.main',
          border: 1,
          borderColor: 'tertiary.main',
          color: 'secondary.main',
          fontSize: '0.8125rem',
          lineHeight: '1rem',
          borderRadius: 2.5,
          justifyContent: 'flex-start',
          textAlign: 'left',
          '&:hover': {
            cursor: 'text',
            borderColor: 'secondary.main',
          },
          '&:focus-visible': {
            outline: '2px solid #202124',
            outlineOffset: 0,
          },
        }}
        onClick={onSearchButtonClick}
      >
        <Search sx={{ mr: 1 }} />
        {search || 'Search reports...'}
      </ButtonBase>
      <Modal
        closeAfterTransition
        keepMounted
        open={isOpen}
        onClose={closeModal}
      >
        <Transition
          in={isOpen}
          width={buttonRef.current?.clientWidth}
          onEntered={() => {
            inputRef.current?.focus();
          }}
          onExited={() => {
            buttonRef.current?.focus();
          }}
        >
          <SearchResultsList
            inputRef={inputRef}
            search={search}
            setSearch={setSearch}
            onInsightClick={handleInsightClick}
            closeModal={closeModal}
          />
        </Transition>
      </Modal>
    </Box>
  );
}
